import api from './api';
import type { ICourse } from '../types/course';
import type { IBatch } from '../types/batch';

const getAllPublishedCourses = async (): Promise<ICourse[]> => {
  try {
    const response = await api.get<ICourse[]>('/courses');
    return response.data;
  } catch (error: any) {
    throw new Error('Failed to fetch courses.');
  }
};

const getAllCoursesForAdmin = async (): Promise<ICourse[]> => {
  try {
    const response = await api.get<ICourse[]>('/courses/all');
    return response.data;
  } catch (error: any) {
    throw new Error('Failed to fetch all courses for management.');
  }
};

const getCourseBySlug = async (slug: string): Promise<ICourse> => {
  try {
    const response = await api.get<ICourse>(`/courses/slug/${slug}`);
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to fetch course details.');
  }
};

const getCourseForEdit = async (id: string): Promise<ICourse> => {
  try {
    const response = await api.get<ICourse>(`/courses/${id}/full`);
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to fetch full course details.');
  }
};

const getCourseDetailsForMentor = async (courseId: string): Promise<{ course: ICourse; batches: IBatch[] }> => {
  try {
    const response = await api.get(`/courses/${courseId}/details`);
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to fetch course details.');
  }
};

const createCourse = async (payload: Partial<ICourse>): Promise<{ message: string; course: ICourse }> => {
  try {
    const response = await api.post<{ message: string; course: ICourse }>('/courses', payload);
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to create course.');
  }
};

const updateCourse = async (id: string, payload: Partial<ICourse>): Promise<{ message: string; course: ICourse }> => {
  try {
    const response = await api.put<{ message: string; course: ICourse }>(`/courses/${id}`, payload);
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to update course.');
  }
};

const deleteCourse = async (id: string): Promise<string> => {
  try {
    const response = await api.delete<{ message: string }>(`/courses/${id}`);
    return response.data.message;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to delete course.');
  }
};

const toggleCoursePublication = async (id: string): Promise<{ message: string; course: ICourse }> => {
    try {
      const response = await api.patch<{ message: string; course: ICourse }>(`/courses/${id}/publish`);
      return response.data;
    } catch (error: any) {
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      throw new Error('Failed to update course publication status.');
    }
  };

// ✅ CORRECTED: The secure material access function now handles file downloads properly.
const getSecureMaterial = async (courseId: string, moduleId: string, materialIndex: number): Promise<void> => {
  try {
    const response = await api.get(
      `/courses/${courseId}/material/${moduleId}/${materialIndex}`,
      {
        responseType: 'blob',
      }
    );

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;

    const contentDisposition = response.headers['content-disposition'];
    let filename = 'downloaded-material';
    if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch && filenameMatch.length > 1) {
            filename = filenameMatch[1];
        }
    }
    
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();

    link.parentNode?.removeChild(link);
    window.URL.revokeObjectURL(url);

  } catch (error: any) {
    if (error.response && error.response.data instanceof Blob) {
        const errorText = await error.response.data.text();
        const errorJson = JSON.parse(errorText);
        throw new Error(errorJson.message || 'Failed to download material.');
    } else {
        throw new Error(error.response?.data?.message || 'Failed to download material.');
    }
  }
};

// Add this function to your courseService file
const getCourseById = async (id: string): Promise<ICourse> => {
  try {
    const response = await api.get<ICourse>(`/courses/${id}`);
    return response.data;
  } catch (error: any) {
    if (error.response?.data?.message) {
      throw new Error(error.response.data.message);
    }
    throw new Error('Failed to fetch course details.');
  }
};

const getEnrolledCourseById = async (id: string): Promise<ICourse> => {
  try {
    const response = await api.get<ICourse>(`/courses/my-courses/${id}`);
    return response.data;
  } catch (error: any) {
    throw new Error(error.response?.data?.message || 'Failed to fetch enrolled course details.');
  }
};

// ... make sure it's exported in your courseService object


const courseService = {
    getAllPublishedCourses,
    getAllCoursesForAdmin,
    getCourseBySlug,
    getCourseForEdit,
    getCourseDetailsForMentor,
    createCourse,
    updateCourse,
    deleteCourse,
    toggleCoursePublication,
    getSecureMaterial,
    getCourseById,
    getEnrolledCourseById
};

export default courseService;